import { Tenant } from './types';

// GUID pattern (e.g. "97eab0c1-2894-4c12-91ad-a081654a90e3")
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Ensures a tenant name has the `.onmicrosoft.com` domain suffix.
 * - Already a full domain (contains a dot) → returned as-is.
 * - A GUID → returned as-is.
 * - A short name (e.g. "M365DS117641") → appends `.onmicrosoft.com`.
 */
export function ensureDomainSuffix(name: string): string {
  if (!name) return name;
  if (name.includes('.')) return name;
  if (GUID_RE.test(name)) return name;
  return `${name}.onmicrosoft.com`;
}

/**
 * Format ISO date string to user-friendly format
 * @param isoDateString - ISO date string like "2025-10-16T05:51:09.2340259+00:00"
 * @returns Formatted date string like "Oct 16, 2025, 5:51 AM"
 */
export function formatDateTimeUTC(isoDateString: string | null | undefined): string {
  if (!isoDateString || isoDateString === 'Never') {
    return 'Never';
  }

  try {
    const date = new Date(isoDateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return isoDateString; // Return original string if invalid
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC'
    }).format(date);
  } catch (error) {
    return isoDateString; // Return original string if parsing fails
  }
}

export function getCoveragePercent(coverage: string): number {
  const [num, total] = coverage.split('/').map(Number);
  return total ? (num / total) * 100 : 0;
}

export function scopeTenants(
  tenants: Tenant[] | undefined,
  selectedSet: Set<string>,
  selectedIsAll: boolean,
) {
  const base = tenants ?? [];
  return selectedIsAll ? base : base.filter((t) => selectedSet.has(t.name));
}

export function summarizeCoverage(scoped: Tenant[]) {
  const totalSel = scoped.length;
  const availableSel = scoped.filter((t) => t.status === 'Available').length;
  const partiallyDeployedSel = scoped.filter((t) => t.status === 'Partially Deployed').length;
  const percent = totalSel ? (availableSel / totalSel) * 100 : 0;

  return {
    label: `${availableSel}/${totalSel}`,
    percent,
    hasPartiallyDeployed: partiallyDeployedSel > 0,
    partiallyDeployedCount: partiallyDeployedSel,
  };
}

/**
 * Map deployment status from API to Tenant status
 */
export function mapDeploymentStatus(deploymentStatus: string): 'Available' | 'Not Available' | 'Partially Deployed' {
  switch (deploymentStatus?.toLowerCase()) {
    case 'deployed':
      return 'Available';
    case 'partiallydeployed':
      return 'Partially Deployed';
    case 'undeployed':
      return 'Not Available';
    default:
      return 'Not Available';
  }
}

/**
 * Map EntityMetadataItem to Tenant format for TenantDetailsTable
 */
export function mapEntityMetadataToTenant(item: any): Tenant {
  return {
    id: item.tenantId,
    name: item.tenantName,
    domain: item.tenantName,
    status: mapDeploymentStatus(item.deploymentStatus),
    lastScanned: item.lastRefreshed,
    lastDeployed: item.lastDeployedTime,
    deployer: item.deployedBy || undefined,
  };
}

// Utility function to compare arrays
export function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((val, i) => val === b[i]);
}

/**
 * Item with deployment counts for sorting
 */
export interface DeploymentItem {
  id: string;
  deployedCount: number;
  totalCount: number;
}

/**
 * Sort items by deployment percentage ascending (0% → partial → 100%)
 * Use for sorting tenants/policies by deployment status: not deployed (red) → partial (amber) → fully deployed (green)
 * 
 * @param items - Array of items with id, deployedCount, and totalCount
 * @returns Object with sorted array and the first item's id (or null if empty)
 */
export function sortByDeploymentPercent<T extends DeploymentItem>(
  items: T[]
): { sorted: T[]; firstId: string | null } {
  if (items.length === 0) {
    return { sorted: [], firstId: null };
  }

  const sorted = [...items].sort((a, b) => {
    const aPercent = a.totalCount > 0 ? (a.deployedCount / a.totalCount) * 100 : 0;
    const bPercent = b.totalCount > 0 ? (b.deployedCount / b.totalCount) * 100 : 0;
    return aPercent - bPercent;
  });

  return { sorted, firstId: sorted[0].id };
}
